import { useState, useEffect, useRef } from 'react'
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import rrwebPlayer from 'rrweb-player'
import 'rrweb-player/dist/style.css'

// Custom styles to counter-scale the controls so they remain readable
// When player is scaled down, we scale the controller back up
const getPlayerControlsStyle = (scale: number) => `
  .rr-controller {
    transform: scale(${1 / scale}) !important;
    transform-origin: bottom left !important;
    width: ${100 * scale}% !important;
    margin-top: ${-80 * (1 - scale)}px !important;
  }
`

interface UATSession {
  id: string
  user_agent: string
  screen_width: number
  screen_height: number
  page_url: string
  started_at: string
  ended_at: string | null
  event_count: number
  feedback_count: number
}

interface UATFeedback {
  id: string
  session_id: string
  feedback_type: string
  message: string
  rating: number | null
  page_url: string
  created_at: string
  user_name?: string
  user_agent?: string
  screen_width?: number
  screen_height?: number
  screenshot_count?: number
  team_notes?: string
}

interface UATScreenshot {
  id: string
  filename: string
  mime_type: string
  file_size: number
  captured_at?: string
}

type ViewType = 'sessions' | 'feedback'

const API_BASE = '/api/uat'

export default function System() {
  const queryClient = useQueryClient()
  const [activeView, setActiveView] = useState<ViewType>('sessions')
  const [selectedSession, setSelectedSession] = useState<UATSession | null>(null)
  const [selectedFeedback, setSelectedFeedback] = useState<UATFeedback | null>(null)
  const [screenshots, setScreenshots] = useState<UATScreenshot[]>([])
  const [playerLoading, setPlayerLoading] = useState(false)
  const [playerError, setPlayerError] = useState<string | null>(null)
  const [fullscreenMode, setFullscreenMode] = useState(false)
  const [playerScale, setPlayerScale] = useState(1)
  const [expandedSessions, setExpandedSessions] = useState<Set<string>>(new Set())
  const [sessionToDelete, setSessionToDelete] = useState<UATSession | null>(null)
  const [editingNotes, setEditingNotes] = useState('')
  const playerContainerRef = useRef<HTMLDivElement>(null)
  const playerWrapperRef = useRef<HTMLDivElement>(null)
  const playerRef = useRef<any>(null)

  // Fetch sessions
  const { data: sessions = [], isLoading: sessionsLoading, refetch: refetchSessions } = useQuery<UATSession[]>({
    queryKey: ['uat-sessions'],
    queryFn: async () => {
      const response = await fetch(`${API_BASE}/sessions`)
      if (!response.ok) throw new Error('Failed to load sessions')
      const data = await response.json()
      return data.sessions || []
    },
    staleTime: 30000,
  })

  // Fetch feedback
  const { data: feedback = [], isLoading: feedbackLoading, refetch: refetchFeedback } = useQuery<UATFeedback[]>({
    queryKey: ['uat-feedback'],
    queryFn: async () => {
      const response = await fetch(`${API_BASE}/feedback`)
      if (!response.ok) throw new Error('Failed to load feedback')
      const data = await response.json()
      return data.feedback || []
    },
    staleTime: 30000,
  })

  // Delete session mutation
  const deleteSessionMutation = useMutation({
    mutationFn: async (sessionId: string) => {
      const response = await fetch(`${API_BASE}/sessions/${sessionId}`, {
        method: 'DELETE',
      })
      if (!response.ok) throw new Error('Failed to delete session')
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['uat-sessions'] })
      if (selectedSession) {
        setSelectedSession(null)
      }
      setSessionToDelete(null)
    },
  })

  // Save team notes mutation
  const saveNotesMutation = useMutation({
    mutationFn: async ({ id, notes }: { id: string; notes: string }) => {
      const response = await fetch(`${API_BASE}/feedback/${id}`, {
        method: 'PATCH',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ team_notes: notes }),
      })
      if (!response.ok) throw new Error('Failed to save notes')
      return response.json()
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['uat-feedback'] })
      if (selectedFeedback) {
        setSelectedFeedback({ ...selectedFeedback, team_notes: editingNotes })
      }
    },
  })

  // Load session for replay
  const loadSessionReplay = async (session: UATSession) => {
    setSelectedSession(session)
    setPlayerLoading(true)
    setPlayerError(null)

    try {
      const response = await fetch(`${API_BASE}/sessions/${session.id}/events`)
      const data = await response.json()

      if (!data.events || data.events.length === 0) {
        setPlayerError('No events found for this session. The session may not have been recorded properly.')
        setPlayerLoading(false)
        return
      }

      // Check if we have the required event types for rrweb-player
      const hasMeta = data.events.some((e: any) => e.type === 4)
      const hasFullSnapshot = data.events.some((e: any) => e.type === 2)

      if (!hasMeta || !hasFullSnapshot) {
        const missing = []
        if (!hasMeta) missing.push('meta event')
        if (!hasFullSnapshot) missing.push('full DOM snapshot')
        setPlayerError(`This session is missing required data (${missing.join(', ')}). Replay not available.`)
        setPlayerLoading(false)
        return
      }

      // Validate event structure
      const validEvents = data.events.filter((e: any) =>
        e && typeof e === 'object' && typeof e.type === 'number' && typeof e.timestamp === 'number'
      )

      if (validEvents.length < data.events.length) {
        console.warn(`Filtered ${data.events.length - validEvents.length} malformed events`)
      }

      if (validEvents.length === 0) {
        setPlayerError('Session events are malformed. Replay not available.')
        setPlayerLoading(false)
        return
      }

      // Destroy previous player if exists
      if (playerRef.current) {
        try {
          playerRef.current.$destroy?.()
        } catch (e) {
          // Ignore destroy errors
        }
        playerRef.current = null
      }

      // Clear previous player container
      if (playerContainerRef.current) {
        playerContainerRef.current.innerHTML = ''
      }

      try {
        // Ensure events are sorted by timestamp
        const sortedEvents = [...validEvents].sort((a: any, b: any) => a.timestamp - b.timestamp)

        console.log('[UAT] Creating player with', sortedEvents.length, 'events')
        console.log('[UAT] First 3 events:', sortedEvents.slice(0, 3).map((e: any) => ({ type: e.type, timestamp: e.timestamp })))

        // Use a small delay to ensure DOM is ready
        await new Promise(resolve => setTimeout(resolve, 100))

        if (!playerContainerRef.current) {
          throw new Error('Player container not available')
        }

        // Get dimensions from the meta event (type 4) for accurate replay
        const metaEvent = sortedEvents.find((e: any) => e.type === 4)
        const sessionWidth = metaEvent?.data?.width || session.screen_width || 1920
        const sessionHeight = metaEvent?.data?.height || session.screen_height || 1080

        console.log('[UAT] Using dimensions from meta event:', sessionWidth, 'x', sessionHeight)

        // Calculate scale to fit in container
        const containerWidth = fullscreenMode ? window.innerWidth - 40 : 900
        const containerHeight = fullscreenMode ? window.innerHeight - 150 : 550

        // Calculate scale to fit both width and height
        const scaleX = containerWidth / sessionWidth
        const scaleY = containerHeight / sessionHeight
        const scale = Math.min(scaleX, scaleY, 1) // Never scale up, only down

        setPlayerScale(scale)

        // Create player at full resolution for accuracy
        playerRef.current = new rrwebPlayer({
          target: playerContainerRef.current,
          props: {
            events: sortedEvents,
            width: sessionWidth,
            height: sessionHeight,
            autoPlay: false,
            showController: true,
            speedOption: [0.1, 0.25, 0.5, 1, 2, 4],
            maxScale: 1,
            skipInactive: true,
            showWarning: true,
            showDebug: false,
            mouseTail: {
              duration: 500,
              lineCap: 'round',
              lineWidth: 3,
              strokeStyle: 'red',
            },
            UNSAFE_replayCanvas: false,
            useVirtualDom: false,
            triggerFocus: false,
            insertStyleRules: [
              'iframe { transform: none !important; }',
              'html, body { overflow: visible !important; }',
            ],
          },
        })

        console.log('[UAT] Player created at', sessionWidth, 'x', sessionHeight, 'with CSS scale:', scale)
      } catch (playerErr) {
        console.error('[UAT] rrweb-player error:', playerErr)
        setPlayerError(`Failed to initialize replay player: ${playerErr instanceof Error ? playerErr.message : 'Unknown error'}`)
        if (playerContainerRef.current) {
          playerContainerRef.current.innerHTML = ''
        }
      }
    } catch (err) {
      console.error('[UAT] Error loading session:', err)
      setPlayerError('Failed to load session replay')
    } finally {
      setPlayerLoading(false)
    }
  }

  // Load screenshots for feedback
  const loadScreenshots = async (feedbackItem: UATFeedback) => {
    setSelectedFeedback(feedbackItem)
    setEditingNotes(feedbackItem.team_notes || '')
    try {
      // First try to load screenshots by feedback_id
      const feedbackResponse = await fetch(`${API_BASE}/feedback/${feedbackItem.id}/screenshots`)
      if (feedbackResponse.ok) {
        const feedbackData = await feedbackResponse.json()
        if (feedbackData.screenshots && feedbackData.screenshots.length > 0) {
          setScreenshots(feedbackData.screenshots)
          return
        }
      }

      // Fall back to session screenshots if no feedback screenshots found
      if (feedbackItem.session_id) {
        const sessionResponse = await fetch(`${API_BASE}/sessions/${feedbackItem.session_id}/screenshots`)
        if (sessionResponse.ok) {
          const sessionData = await sessionResponse.json()
          setScreenshots(sessionData.screenshots || [])
          return
        }
      }

      setScreenshots([])
    } catch (err) {
      console.error('Failed to load screenshots:', err)
      setScreenshots([])
    }
  }

  // Inject custom styles for player controls
  useEffect(() => {
    const styleId = 'uat-player-controls-style'
    let style = document.getElementById(styleId) as HTMLStyleElement
    if (!style) {
      style = document.createElement('style')
      style.id = styleId
      document.head.appendChild(style)
    }
    style.textContent = getPlayerControlsStyle(playerScale)

    return () => {
      const s = document.getElementById(styleId)
      if (s) s.remove()
    }
  }, [playerScale])

  // Refetch when view changes
  useEffect(() => {
    if (activeView === 'sessions') {
      refetchSessions()
    } else {
      refetchFeedback()
    }
  }, [activeView, refetchSessions, refetchFeedback])

  const formatDate = (dateStr: string) => {
    return new Date(dateStr).toLocaleString()
  }

  const formatDuration = (start: string, end: string | null) => {
    if (!end) return 'In progress'
    const duration = new Date(end).getTime() - new Date(start).getTime()
    const seconds = Math.floor(duration / 1000)
    const minutes = Math.floor(seconds / 60)
    if (minutes > 0) return `${minutes}m ${seconds % 60}s`
    return `${seconds}s`
  }

  const getFeedbackTypeBadge = (type: string) => {
    switch (type) {
      case 'bug':
        return 'badge-error'
      case 'suggestion':
        return 'badge-info'
      case 'confusion':
        return 'badge-warning'
      default:
        return 'badge-ghost'
    }
  }

  const getFeedbackTypeIcon = (type: string) => {
    switch (type) {
      case 'bug':
        return '🐛'
      case 'suggestion':
        return '💡'
      case 'confusion':
        return '😕'
      default:
        return '💬'
    }
  }

  // Group feedback by session
  const groupFeedbackBySession = (feedbackList: UATFeedback[]) => {
    const grouped: Map<string, UATFeedback[]> = new Map()
    const noSession: UATFeedback[] = []

    for (const item of feedbackList) {
      if (item.session_id) {
        const existing = grouped.get(item.session_id) || []
        existing.push(item)
        grouped.set(item.session_id, existing)
      } else {
        noSession.push(item)
      }
    }

    // Convert to array and sort by most recent feedback in each group
    const sortedGroups = Array.from(grouped.entries())
      .map(([sessionId, items]) => ({
        sessionId,
        items: items.sort((a, b) => new Date(b.created_at).getTime() - new Date(a.created_at).getTime()),
        latestDate: Math.max(...items.map(i => new Date(i.created_at).getTime())),
      }))
      .sort((a, b) => b.latestDate - a.latestDate)

    return { grouped: sortedGroups, noSession }
  }

  const toggleSessionExpand = (sessionId: string) => {
    setExpandedSessions(prev => {
      const next = new Set(prev)
      if (next.has(sessionId)) {
        next.delete(sessionId)
      } else {
        next.add(sessionId)
      }
      return next
    })
  }

  const { grouped: groupedFeedback, noSession: ungroupedFeedback } = groupFeedbackBySession(feedback)
  const activeSessions = sessions.filter(s => s.event_count > 0)

  return (
    <div className="p-8">
      <div className="mb-6">
        <h1 className="page-title mb-2">UAT Sessions</h1>
        <p className="text-base-content opacity-70">User Acceptance Testing Sessions and Feedback</p>
      </div>

      {/* Error Alert */}
      {playerError && (
        <div className="alert alert-error mb-6">
          <svg xmlns="http://www.w3.org/2000/svg" className="h-6 w-6 shrink-0 stroke-current" fill="none" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M10 14l-2-2m0 0l-2-2m2 2l2-2m-2 2l-2 2m2-2l2 2m7-2a9 9 0 11-18 0 9 9 0 0118 0z" />
          </svg>
          <span>{playerError}</span>
        </div>
      )}

      {/* Tabs */}
      <div className="tabs tabs-bordered mb-6">
        <input
          type="radio"
          name="uat_tabs"
          className="tab"
          aria-label={`Sessions (${activeSessions.length})`}
          checked={activeView === 'sessions'}
          onChange={() => setActiveView('sessions')}
        />
        <input
          type="radio"
          name="uat_tabs"
          className="tab"
          aria-label={`Feedback (${feedback.length})`}
          checked={activeView === 'feedback'}
          onChange={() => setActiveView('feedback')}
        />
      </div>

      {/* Sessions View */}
      {activeView === 'sessions' && (
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
          {/* Sessions List */}
          <div className="card bg-base-100 shadow-xl border border-base-300">
            <div className="card-body p-4">
              <div className="flex justify-between items-center mb-4">
                <h2 className="card-title text-lg">Sessions</h2>
                <button
                  className="btn btn-sm btn-ghost"
                  onClick={() => refetchSessions()}
                  disabled={sessionsLoading}
                >
                  <svg xmlns="http://www.w3.org/2000/svg" className="h-4 w-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15" />
                  </svg>
                </button>
              </div>
              <div className="space-y-2 max-h-[600px] overflow-y-auto">
                {sessionsLoading && (
                  <div className="flex justify-center py-8">
                    <span className="loading loading-spinner loading-md"></span>
                  </div>
                )}
                {!sessionsLoading && activeSessions.length === 0 && (
                  <p className="text-center text-base-content opacity-60 py-8">No sessions found</p>
                )}
                {!sessionsLoading && activeSessions.map((session) => (
                  <div
                    key={session.id}
                    onClick={() => loadSessionReplay(session)}
                    className={`p-3 rounded-lg cursor-pointer transition-colors border ${
                      selectedSession?.id === session.id
                        ? 'bg-info bg-opacity-20 border-info'
                        : 'bg-base-200 border-base-300 hover:bg-base-300'
                    }`}
                  >
                    <div className="flex justify-between items-start mb-2">
                      <span className="text-xs font-mono text-base-content opacity-60">
                        {session.id.slice(-12)}
                      </span>
                      <div className="flex items-center gap-2">
                        <span className="badge badge-success badge-sm">{session.event_count} events</span>
                        <button
                          onClick={(e) => {
                            e.stopPropagation()
                            setSessionToDelete(session)
                          }}
                          className="btn btn-ghost btn-xs text-error"
                          title="Delete session"
                        >
                          <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
                          </svg>
                        </button>
                      </div>
                    </div>
                    <div className="text-sm text-base-content mb-1">
                      {formatDate(session.started_at)}
                    </div>
                    <div className="text-xs text-base-content opacity-60">
                      {session.screen_width}x{session.screen_height} • {formatDuration(session.started_at, session.ended_at)}
                    </div>
                    {session.feedback_count > 0 && (
                      <div className="text-xs text-warning font-medium mt-1">
                        📝 {session.feedback_count} feedback
                      </div>
                    )}
                  </div>
                ))}
              </div>
            </div>
          </div>

          {/* Player */}
          <div className={`${fullscreenMode ? 'fixed inset-0 z-50 bg-base-100' : 'lg:col-span-2'} card bg-base-100 shadow-xl border border-base-300 overflow-hidden`}>
            <div className="card-body p-4">
              <div className="flex justify-between items-start mb-4">
                <div>
                  <h2 className="card-title text-lg">Session Replay</h2>
                  {selectedSession && (
                    <p className="text-xs text-base-content opacity-60 mt-1 truncate max-w-md">
                      {selectedSession.page_url} ({selectedSession.screen_width}x{selectedSession.screen_height})
                    </p>
                  )}
                </div>
                {selectedSession && !playerLoading && (
                  <button
                    className="btn btn-sm btn-ghost"
                    onClick={() => setFullscreenMode(!fullscreenMode)}
                  >
                    {fullscreenMode ? 'Exit Fullscreen' : 'Fullscreen'}
                  </button>
                )}
              </div>
              <div className={`flex-1 ${fullscreenMode ? 'h-[calc(100vh-80px)] overflow-auto' : ''}`}>
                {!selectedSession && (
                  <div className="h-[550px] flex items-center justify-center text-base-content opacity-40 bg-base-200 rounded-lg">
                    Select a session to replay
                  </div>
                )}
                {selectedSession && playerLoading && (
                  <div className="h-[550px] flex flex-col items-center justify-center text-base-content opacity-60 bg-base-200 rounded-lg">
                    <span className="loading loading-spinner loading-lg mb-4"></span>
                    Loading replay...
                  </div>
                )}
                {/* Player wrapper with CSS transform for scaling */}
                <div
                  ref={playerWrapperRef}
                  className={`${!selectedSession || playerLoading ? 'hidden' : ''}`}
                  style={{
                    width: selectedSession ? selectedSession.screen_width * playerScale : 'auto',
                    height: selectedSession ? (selectedSession.screen_height * playerScale) + 80 : 'auto',
                    overflow: 'hidden',
                    margin: '0 auto',
                  }}
                >
                  <div
                    ref={playerContainerRef}
                    style={{
                      transform: `scale(${playerScale})`,
                      transformOrigin: 'top left',
                    }}
                  />
                </div>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Feedback View */}
      {activeView === 'feedback' && (
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
          {/* Feedback List */}
          <div className="card bg-base-100 shadow-xl border border-base-300">
            <div className="card-body p-4">
              <div className="flex justify-between items-center mb-4">
                <h2 className="card-title text-lg">User Feedback</h2>
                <button
                  className="btn btn-sm btn-ghost"
                  onClick={() => refetchFeedback()}
                  disabled={feedbackLoading}
                >
                  <svg xmlns="http://www.w3.org/2000/svg" className="h-4 w-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15" />
                  </svg>
                </button>
              </div>
              <div className="space-y-2 max-h-[600px] overflow-y-auto">
                {feedbackLoading && (
                  <div className="flex justify-center py-8">
                    <span className="loading loading-spinner loading-md"></span>
                  </div>
                )}
                {!feedbackLoading && feedback.length === 0 && (
                  <p className="text-center text-base-content opacity-60 py-8">No feedback found</p>
                )}
                {!feedbackLoading && (
                  <>
                    {/* Grouped feedback by session */}
                    {groupedFeedback.map(({ sessionId, items }) => (
                      <div key={sessionId} className="border border-base-300 rounded-lg overflow-hidden">
                        {/* Session header - collapsible */}
                        <div
                          onClick={() => toggleSessionExpand(sessionId)}
                          className="p-3 bg-base-200 cursor-pointer hover:bg-base-300 flex items-center justify-between transition-colors"
                        >
                          <div className="flex items-center gap-2">
                            <span className="text-base-content opacity-60">
                              {expandedSessions.has(sessionId) ? '−' : '+'}
                            </span>
                            <span className="font-mono text-xs text-base-content opacity-60">
                              Session ...{sessionId.slice(-8)}
                            </span>
                            <span className="badge badge-info badge-sm">
                              {items.length} feedback{items.length > 1 ? 's' : ''}
                            </span>
                          </div>
                          <span className="text-xs text-base-content opacity-60">
                            {formatDate(items[0].created_at)}
                          </span>
                        </div>

                        {/* Feedback items in session */}
                        {expandedSessions.has(sessionId) && items.map((item) => (
                          <div
                            key={item.id}
                            onClick={() => loadScreenshots(item)}
                            className={`p-3 border-t border-base-300 cursor-pointer hover:bg-base-200 transition-colors ml-4 ${
                              selectedFeedback?.id === item.id ? 'bg-info bg-opacity-20' : ''
                            }`}
                          >
                            <div className="flex items-center gap-2 mb-2 flex-wrap">
                              <span className={`badge ${getFeedbackTypeBadge(item.feedback_type)} badge-sm`}>
                                {getFeedbackTypeIcon(item.feedback_type)} {item.feedback_type}
                              </span>
                              {item.rating && (
                                <span className="text-warning text-sm">
                                  {'★'.repeat(item.rating)}{'☆'.repeat(5 - item.rating)}
                                </span>
                              )}
                              {item.user_name && (
                                <span className="text-xs text-base-content bg-base-300 px-2 py-0.5 rounded">
                                  {item.user_name}
                                </span>
                              )}
                            </div>
                            <p className="text-sm text-base-content line-clamp-2">{item.message}</p>
                            <div className="text-xs text-base-content opacity-60 mt-2">
                              {formatDate(item.created_at)}
                              {item.screenshot_count && item.screenshot_count > 0 && (
                                <span className="ml-2 text-info">📷 {item.screenshot_count}</span>
                              )}
                            </div>
                          </div>
                        ))}
                      </div>
                    ))}

                    {/* Ungrouped feedback (no session) */}
                    {ungroupedFeedback.length > 0 && (
                      <div className="border border-base-300 rounded-lg overflow-hidden">
                        <div className="p-3 bg-base-200">
                          <span className="text-xs text-base-content opacity-60">No session ({ungroupedFeedback.length})</span>
                        </div>
                        {ungroupedFeedback.map((item) => (
                          <div
                            key={item.id}
                            onClick={() => loadScreenshots(item)}
                            className={`p-3 border-t border-base-300 cursor-pointer hover:bg-base-200 transition-colors ${
                              selectedFeedback?.id === item.id ? 'bg-info bg-opacity-20' : ''
                            }`}
                          >
                            <div className="flex items-center gap-2 mb-2 flex-wrap">
                              <span className={`badge ${getFeedbackTypeBadge(item.feedback_type)} badge-sm`}>
                                {getFeedbackTypeIcon(item.feedback_type)} {item.feedback_type}
                              </span>
                              {item.rating && (
                                <span className="text-warning text-sm">
                                  {'★'.repeat(item.rating)}{'☆'.repeat(5 - item.rating)}
                                </span>
                              )}
                              {item.user_name && (
                                <span className="text-xs text-base-content bg-base-300 px-2 py-0.5 rounded">
                                  {item.user_name}
                                </span>
                              )}
                            </div>
                            <p className="text-sm text-base-content line-clamp-2">{item.message}</p>
                            <div className="text-xs text-base-content opacity-60 mt-2">
                              {formatDate(item.created_at)}
                              {item.screenshot_count && item.screenshot_count > 0 && (
                                <span className="ml-2 text-info">📷 {item.screenshot_count}</span>
                              )}
                            </div>
                          </div>
                        ))}
                      </div>
                    )}
                  </>
                )}
              </div>
            </div>
          </div>

          {/* Feedback Detail */}
          <div className="card bg-base-100 shadow-xl border border-base-300">
            <div className="card-body p-4">
              <h2 className="card-title text-lg mb-4">Feedback Details</h2>
              {!selectedFeedback ? (
                <div className="h-[500px] flex items-center justify-center text-base-content opacity-40 bg-base-200 rounded-lg">
                  Select feedback to view details
                </div>
              ) : (
                <div className="space-y-4">
                  {/* Feedback Info */}
                  <div className="space-y-3">
                    <div className="flex items-center gap-2 flex-wrap">
                      <span className={`badge ${getFeedbackTypeBadge(selectedFeedback.feedback_type)}`}>
                        {getFeedbackTypeIcon(selectedFeedback.feedback_type)} {selectedFeedback.feedback_type}
                      </span>
                      {selectedFeedback.rating && (
                        <span className="text-warning text-lg">
                          {'★'.repeat(selectedFeedback.rating)}{'☆'.repeat(5 - selectedFeedback.rating)}
                        </span>
                      )}
                      {selectedFeedback.user_name && (
                        <span className="text-sm text-base-content bg-base-200 px-3 py-1 rounded">
                          From: {selectedFeedback.user_name}
                        </span>
                      )}
                    </div>

                    <div className="bg-base-200 p-4 rounded-lg border border-base-300">
                      <p className="text-base-content whitespace-pre-wrap text-sm">{selectedFeedback.message}</p>
                    </div>

                    <div className="grid grid-cols-2 gap-4 text-sm">
                      <div>
                        <span className="text-base-content opacity-60 text-xs">Submitted:</span>
                        <p className="text-base-content">{formatDate(selectedFeedback.created_at)}</p>
                      </div>
                      <div>
                        <span className="text-base-content opacity-60 text-xs">Page:</span>
                        <p className="text-base-content truncate text-xs">{selectedFeedback.page_url}</p>
                      </div>
                      {selectedFeedback.screen_width && (
                        <div>
                          <span className="text-base-content opacity-60 text-xs">Screen:</span>
                          <p className="text-base-content">{selectedFeedback.screen_width}x{selectedFeedback.screen_height}</p>
                        </div>
                      )}
                      {selectedFeedback.session_id && (
                        <div>
                          <span className="text-base-content opacity-60 text-xs">Session:</span>
                          <p className="text-base-content font-mono text-xs">{selectedFeedback.session_id.slice(-12)}</p>
                        </div>
                      )}
                    </div>

                    {/* View Session Button */}
                    {selectedFeedback.session_id && (
                      <button
                        className="btn btn-primary w-full btn-sm"
                        onClick={() => {
                          const session = sessions.find(s => s.id === selectedFeedback.session_id)
                          if (session) {
                            setActiveView('sessions')
                            setTimeout(() => loadSessionReplay(session), 100)
                          }
                        }}
                      >
                        View Session Replay
                      </button>
                    )}

                    {/* Team Notes */}
                    <div className="divider my-2"></div>
                    <div>
                      <h4 className="font-medium mb-2 text-base-content text-sm">Team Notes</h4>
                      <textarea
                        value={editingNotes}
                        onChange={(e) => setEditingNotes(e.target.value)}
                        placeholder="Add internal notes about this feedback..."
                        className="textarea textarea-bordered w-full text-sm resize-none"
                        rows={4}
                      />
                      <div className="flex justify-between items-center mt-3">
                        <span className="text-xs text-base-content opacity-60">
                          {editingNotes !== (selectedFeedback.team_notes || '') && '• Unsaved changes'}
                        </span>
                        <button
                          className="btn btn-primary btn-sm"
                          onClick={() => saveNotesMutation.mutate({ id: selectedFeedback.id, notes: editingNotes })}
                          disabled={saveNotesMutation.isPending || editingNotes === (selectedFeedback.team_notes || '')}
                        >
                          {saveNotesMutation.isPending ? 'Saving...' : 'Save Notes'}
                        </button>
                      </div>
                    </div>
                  </div>

                  {/* Screenshots */}
                  {screenshots.length > 0 && (
                    <div>
                      <h4 className="font-medium mb-2 text-base-content text-sm">Screenshots</h4>
                      <div className="grid grid-cols-2 gap-2">
                        {screenshots.map((screenshot) => (
                          <a
                            key={screenshot.id}
                            href={`${API_BASE}/screenshots/${screenshot.id}`}
                            target="_blank"
                            rel="noopener noreferrer"
                            className="block bg-base-200 rounded overflow-hidden hover:ring-2 ring-info transition-all"
                          >
                            <img
                              src={`${API_BASE}/screenshots/${screenshot.id}`}
                              alt={screenshot.filename}
                              className="w-full h-24 object-cover"
                            />
                            <div className="p-2 text-xs text-base-content opacity-60 truncate">
                              {screenshot.filename}
                            </div>
                          </a>
                        ))}
                      </div>
                    </div>
                  )}
                </div>
              )}
            </div>
          </div>
        </div>
      )}

      {/* Delete Confirmation Modal */}
      {sessionToDelete && (
        <dialog className="modal modal-open">
          <div className="modal-box max-w-md">
            <div className="flex items-center gap-3 mb-4">
              <div className="w-10 h-10 rounded-full bg-error bg-opacity-20 flex items-center justify-center">
                <svg className="w-6 h-6 text-error" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z" />
                </svg>
              </div>
              <h3 className="font-semibold text-base-content">Delete Session</h3>
            </div>

            <p className="text-base-content opacity-80 mb-2">
              Are you sure you want to delete this session?
            </p>
            <p className="text-sm text-base-content opacity-60 mb-4">
              This will permanently delete:
            </p>
            <ul className="text-sm text-base-content opacity-80 mb-4 ml-4 list-disc space-y-1">
              <li>Session <span className="font-mono text-xs">{sessionToDelete.id.slice(-12)}</span></li>
              <li>{sessionToDelete.event_count} recorded events</li>
              {sessionToDelete.feedback_count > 0 && (
                <li>{sessionToDelete.feedback_count} feedback item(s) and screenshots</li>
              )}
              <li>All associated replay data</li>
            </ul>
            <p className="text-sm text-error font-medium">
              This action cannot be undone.
            </p>

            <div className="modal-action mt-6">
              <button
                onClick={() => setSessionToDelete(null)}
                disabled={deleteSessionMutation.isPending}
                className="btn btn-ghost"
              >
                Cancel
              </button>
              <button
                onClick={() => deleteSessionMutation.mutate(sessionToDelete.id)}
                disabled={deleteSessionMutation.isPending}
                className="btn btn-error"
              >
                {deleteSessionMutation.isPending ? 'Deleting...' : 'Delete Session'}
              </button>
            </div>
          </div>
          <form method="dialog" className="modal-backdrop" onClick={() => setSessionToDelete(null)}>
            <button>close</button>
          </form>
        </dialog>
      )}
    </div>
  )
}
